import { Box } from '@chakra-ui/react';

import { useAvatarManifest } from '../../assets/data/avatarManifest';
import { ICohort, ICohortUser } from '../../utils/interfaces/DbInterfaces';
import { getPathfinderUrl } from '../../utils/pathsUtil';
import CompanionCanvas from '../CompanionCanvas/CompanionCanvas';
import SectionWrapper from '../SectionWrapper';

import styles from './Cohort.module.scss';

interface ICohortProps {
  id: string;
  cohort?: ICohort;
  className?: string;
}

// Helper function to get league name
const getLeagueName = (tier: string) => {
  switch (tier) {
    case 'wd':
      return 'Wood League';
    case 'br':
      return 'Bronze League';
    case 'si':
      return 'Silver League';
    case 'go':
      return 'Gold League';
    case 'pl':
      return 'Platinum League';
    case 'di':
      return 'Diamond League';
    case 'll':
      return 'Logic Legend League';
    case 'ss':
      return 'Script Samurai League';
    case 'bs':
      return 'Bug Slayer League';
    case 'sc':
      return 'Supreme Coder League';
    case 'mc':
      return 'Master Coder League';
    default:
      return 'Wood League';
  }
};

// Helper function to render avatar
const renderAvatar = (user: ICohortUser, avatarManifest: Record<string, any>) => {
  const avatarBaseUrl = `${getPathfinderUrl()}/pathfinder/league/assets`;
  // If user has an avatar, render it using CompanionCanvas
  console.log('user.avatar.layers?.mood ', user.avatar?.layers?.mood);
  console.log('avatarManifest?.[user.avatar.layers.mood] ', avatarManifest);

  if (user.avatar) {
    return (
      <div className={styles.avatar} style={{ position: 'relative' }}>
        <CompanionCanvas appearance={user.avatar} width={48} height={48} />
        {user.avatar.layers?.mood && avatarManifest?.[user.avatar.layers.mood] && (
          <Box
            position='absolute'
            top='-10px'
            right='-24px'
            bg='white'
            borderRadius='8px 8px 8px 0'
            border='1px solid #F3F3F3'
            minW='32px'
            minH='32px'
            p='6px'
            display='flex'
            alignItems='center'
            justifyContent='center'
            zIndex={2}
            _dark={{
              bg: 'gray.800',
              border: '1px solid #282A35',
            }}
          >
            {/* <Img src={avatarAssets[avatar.layers.mood].svg} alt={avatarAssets[avatar.layers.mood].name} style={{ width: 16, height: 16 }} /> */}
            <img
              src={avatarManifest[user.avatar.layers.mood].svg}
              alt={avatarManifest[user.avatar.layers.mood].name}
              style={{ width: 16, height: 16 }}
            />
          </Box>
        )}
      </div>
    );
  }

  // Fallback to base lynx SVG, slightly smaller (32x32) and centered
  return (
    <div className={styles.avatar} style={{ backgroundColor: 'transparent' }}>
      <img
        src={`${avatarBaseUrl}/images/companion/base/base_lynx.svg`}
        alt='Base Lynx'
        width={32}
        height={32}
        style={{ display: 'block', objectFit: 'contain' }}
      />
    </div>
  );
};

const Cohort = ({ cohort, className, id }: ICohortProps) => {
  const avatarManifest = useAvatarManifest();
  if (!cohort || !cohort.users.length) {
    return null;
  }

  const leagueName = getLeagueName(cohort.users[0]?.tier || 'br');
  const headerText = `${leagueName} Top ${cohort.totalUsers}`;

  // Sort users by weeklyXP descending, then by nickname ascending
  const sortedUsers = [...cohort.users].sort((a, b) => {
    const xpDiff = (b.weeklyXP || 0) - (a.weeklyXP || 0);
    if (xpDiff !== 0) return xpDiff;
    return a.nickname.localeCompare(b.nickname);
  });

  return (
    <SectionWrapper headerText={headerText} className={`${styles.body_wrapper} ${className}`} id='cohort'>
      <div className={styles.cohortList}>
        {sortedUsers.map((user, index) => (
          <div key={user.id || user.nickname || `user-${index}`} className={styles.cohortItem}>
            <div className={styles.leftSection}>
              <div className={styles.rankCircle}>{index + 1}</div>
              {renderAvatar(user, avatarManifest)}
              <div className={styles.userInfo}>
                <div className={styles.nickname}>
                  <a href={`${window.location.origin}/${user.nickname}`} className={styles.usernameLink} target='_blank' rel='noopener noreferrer'>
                    {user.nickname}
                  </a>
                  <span className={styles.tagsRow}>
                    {(() =>
                      user.nickname &&
                      id &&
                      user.nickname.trim().toLowerCase() === id.trim().toLowerCase() && <span className={styles.youLabel}>Current profile</span>)()}
                    {user.booster && (
                      <span className={styles.boosterLabel}>
                        <svg
                          className={styles.boosterIcon}
                          fill='none'
                          viewBox='0 0 24 24'
                          strokeWidth='1.5'
                          stroke='currentColor'
                          aria-hidden='true'
                          xmlns='http://www.w3.org/2000/svg'
                          style={{ width: 12, height: 12 }}
                        >
                          <path strokeLinecap='round' strokeLinejoin='round' d='M3.75 13.5L14.25 2.25 12 10.5h8.25L9.75 21.75 12 13.5H3.75Z' />
                        </svg>
                        BOOSTED
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.xpAmount}>{(user.weeklyXP || 0).toLocaleString()} XP</div>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
};

Cohort.defaultProps = {
  cohort: undefined,
  className: '',
};

export default Cohort;
